import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Download,
  FileText,
  FolderOpen,
  Gavel,
  Globe,
  ListChecks,
  Loader2,
  Pencil,
  Sparkles,
  Users,
  Volume2,
} from 'lucide-react';
import { useAsyncData } from '../features/directory/useAsyncData';
import { useArchiveExport } from '../features/directory/useArchiveExport';
import { displaySummaryText } from '../features/recording/summaryRules';
import { plural, t } from '../lib/i18n';
import { IS_WEB } from '../lib/platform';
import {
  cardClass,
  formatDate,
  formatRelativeDate,
  initials,
  inputClass,
  pageTitle,
  primaryButton,
  secondaryButton,
  sectionTitle,
} from './ui/styles';

interface CompanyProfileViewProps {
  companyId: string;
  isDarkMode: boolean;
  revision: number;
  onBack: () => void;
  onOpenMeeting: (meetingId: string) => void;
  onOpenPerson: (personId: string) => void;
  onAskAI: (prompt: string) => void;
  onChanged: () => void;
  onNotify: (kind: 'success' | 'error' | 'info', text: string) => void;
}

/**
 * Şirket profili — müşteri arşivi: şirkete bağlı kişiler, o kuruma dair
 * tüm toplantı kayıtları (ses, transkript, özet), kararlar ve açık işler.
 */
export const CompanyProfileView: React.FC<CompanyProfileViewProps> = ({
  companyId,
  isDarkMode,
  revision,
  onBack,
  onOpenMeeting,
  onOpenPerson,
  onAskAI,
  onChanged,
  onNotify,
}) => {
  const [localRevision, setLocalRevision] = useState(0);
  const profile = useAsyncData(
    () => window.zabit.getCompanyProfile(companyId),
    `${companyId}:${revision}:${localRevision}`
  );
  const exporter = useArchiveExport(onNotify);
  const card = cardClass(isDarkMode);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', domain: '', notes: '' });
  const [isSaving, setIsSaving] = useState(false);
  const company = profile.data?.company;

  useEffect(() => {
    if (company && isEditing) {
      setDraft({ name: company.name, domain: company.domain ?? '', notes: company.notes ?? '' });
    }
  }, [company, isEditing]);

  const save = async () => {
    if (!company) return;
    setIsSaving(true);
    try {
      await window.zabit.updateCompany(company.id, {
        name: draft.name,
        domain: draft.domain.trim() || null,
        notes: draft.notes.trim() || null,
      });
      setIsEditing(false);
      setLocalRevision((v) => v + 1);
      onChanged();
    } catch (err) {
      onNotify('error', (err as Error).message || t('Şirket güncellenemedi.', 'Could not update the company.'));
    } finally {
      setIsSaving(false);
    }
  };

  if (profile.isLoading && !profile.data) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-16 text-[13px] text-neutral-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('Şirket yükleniyor…', 'Loading company…')}
      </div>
    );
  }
  if (profile.error || !profile.data || !company) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center space-y-3">
        <p className="text-[13px] text-rose-400">{profile.error || t('Şirket bulunamadı.', 'Company not found.')}</p>
        <button onClick={onBack} data-native-back className="text-[13px] font-semibold text-[#46CBE2]">{t('Şirketlere dön', 'Back to Companies')}</button>
      </div>
    );
  }

  const { people, meetings, decisions, actionItems } = profile.data;
  const field = `w-full px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${inputClass(isDarkMode)}`;
  const divide = isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100';

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-7 select-none">
      <button onClick={onBack} data-native-back className="flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-neutral-300">
        <ArrowLeft className="w-3.5 h-3.5" /> {t('Şirketler', 'Companies')}
      </button>

      {/* Künye */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-2xl bg-neutral-800 border border-neutral-700/50 text-[#46CBE2] font-bold text-[20px] flex items-center justify-center shrink-0">
            {company.name.charAt(0).toLocaleUpperCase('tr')}
          </div>
          <div className="min-w-0">
            <h1 className={`${pageTitle(isDarkMode)} truncate`}>{company.name}</h1>
            <div className="flex items-center gap-3 flex-wrap text-[12.5px] text-neutral-500 mt-1">
              {company.domain && (
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" /> {company.domain}
                </span>
              )}
              <span>
                {t(`${company.peopleCount} kişi`, plural(company.peopleCount, 'person', 'people'))} ·{' '}
                {t(`${company.meetingCount} toplantı`, plural(company.meetingCount, 'meeting', 'meetings'))}
              </span>
              <span>
                {t('son görüşme', 'last met')} {formatRelativeDate(company.lastMeetingISO)}
              </span>
            </div>
            {company.notes && <p className="text-[12.5px] text-neutral-400 mt-1.5 leading-relaxed">{company.notes}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button onClick={() => setIsEditing((v) => !v)} className={secondaryButton(isDarkMode)}>
            <Pencil className="w-3.5 h-3.5" /> {t('Düzenle', 'Edit')}
          </button>
          <button
            onClick={() => void exporter.run(company.id)}
            disabled={exporter.isRunning || meetings.length === 0}
            className={secondaryButton(isDarkMode)}
            title={
              IS_WEB
                ? t(
                    'Bu şirketin toplantılarını (ses, transkript, özet) ZIP olarak indir',
                    'Download this company’s meetings (audio, transcript, summary) as a ZIP'
                  )
                : t(
                    'Bu şirketin toplantılarını (ses, transkript, özet) bir klasöre indir',
                    'Download this company’s meetings (audio, transcript, summary) to a folder'
                  )
            }
          >
            {exporter.isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {t('Arşivi dışa aktar', 'Export archive')}
          </button>
          <button
            onClick={() =>
              onAskAI(
                t(
                  `${company.name} ile yaptığımız tüm toplantıları, kararları ve açık işleri müşteri özeti olarak çıkar`,
                  `Write a client summary of all our meetings with ${company.name}, the decisions and the open items`
                )
              )
            }
            className={primaryButton}
          >
            <Sparkles className="w-3.5 h-3.5" /> {t('AI\'a sor', 'Ask AI')}
          </button>
        </div>
      </div>

      {(exporter.isRunning || exporter.result) && (
        <div className={`p-3.5 rounded-2xl border text-[12.5px] flex items-center justify-between gap-3 ${card}`}>
          {exporter.isRunning ? (
            <span className="text-neutral-400 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {exporter.progress
                ? `${exporter.progress.done}/${exporter.progress.total} · ${exporter.progress.current ?? ''}`
                : IS_WEB
                  ? t('Hazırlanıyor…', 'Preparing…')
                  : t('Klasör seçiliyor…', 'Choosing a folder…')}
            </span>
          ) : (
            <span className="text-emerald-400">
              {t(
                `${exporter.result!.exported} toplantı aktarıldı.`,
                `Exported ${plural(exporter.result!.exported, 'meeting', 'meetings')}.`
              )}
            </span>
          )}
          {exporter.result && !IS_WEB && (
            <button onClick={exporter.reveal} className="flex items-center gap-1 text-[#46CBE2] font-semibold">
              <FolderOpen className="w-3.5 h-3.5" /> {t('Klasörü aç', 'Open folder')}
            </button>
          )}
        </div>
      )}

      {isEditing && (
        <div className={`p-4 rounded-2xl border space-y-3 ${card}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('Şirket adı', 'Company name')}</span>
              <input className={field} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('E-posta domain\'i', 'Email domain')}</span>
              <input className={field} value={draft.domain} placeholder="acme.com" onChange={(e) => setDraft({ ...draft, domain: e.target.value })} />
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[11px] font-semibold text-neutral-500">{t('Notlar', 'Notes')}</span>
              <textarea
                className={`${field} min-h-[72px]`}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setIsEditing(false)} className="text-[12px] text-neutral-500 hover:text-neutral-300 px-2">
              {t('Vazgeç', 'Cancel')}
            </button>
            <button onClick={() => void save()} disabled={isSaving || !draft.name.trim()} className={primaryButton}>
              {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t('Kaydet', 'Save')}
            </button>
          </div>
        </div>
      )}

      {/* Kişiler */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <Users className="w-3.5 h-3.5 text-emerald-400" /> {t('Kişiler', 'People')} ({people.length})
        </h2>
        {people.length === 0 ? (
          <p className={`p-4 rounded-2xl border text-[13px] text-neutral-500 ${card}`}>
            {t(
              'Bu şirkete bağlı kişi yok. Kişi profilinden şirket seçerek bağlayabilirsin.',
              'No one is linked to this company yet. Open a person’s profile and pick this company.'
            )}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {people.map((p) => (
              <button
                key={p.id}
                onClick={() => onOpenPerson(p.id)}
                className={`p-3 rounded-2xl border flex items-center gap-3 text-left group hover:border-[#3AC8E0]/40 ${card}`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#1DA4BA] to-indigo-500 text-[#F0EEE6] font-bold text-[11px] flex items-center justify-center shrink-0">
                  {initials(p.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-neutral-200 truncate group-hover:text-[#46CBE2]">{p.name}</div>
                  <div className="text-[11.5px] text-neutral-500 truncate">
                    {[p.role, t(`${p.meetingCount} toplantı`, plural(p.meetingCount, 'meeting', 'meetings'))]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Toplantı arşivi */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <FileText className="w-3.5 h-3.5 text-[#46CBE2]" /> {t('Toplantı arşivi', 'Meeting archive')} ({meetings.length})
        </h2>
        {meetings.length === 0 ? (
          <p className={`p-4 rounded-2xl border text-[13px] text-neutral-500 ${card}`}>
            {t('Bu şirketle kayıtlı toplantı yok.', 'No meetings recorded with this company yet.')}
          </p>
        ) : (
          <div className={`rounded-2xl border divide-y ${card} ${divide}`}>
            {meetings.map((m) => (
              <button key={m.id} onClick={() => onOpenMeeting(m.id)} className="w-full text-left p-4 hover:bg-white/[0.02] group">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[14px] font-semibold text-[#F0EEE6] truncate group-hover:text-[#46CBE2]">{m.title}</span>
                  <span className="flex items-center gap-2 text-[12px] text-neutral-500 shrink-0">
                    {m.hasAudio && <Volume2 className="w-3.5 h-3.5" aria-label={t('Ses kaydı var', 'Has audio')} />}
                    {m.hasTranscript && <FileText className="w-3.5 h-3.5" aria-label={t('Transkript var', 'Has transcript')} />}
                    {formatDate(m.recordedAtISO)}
                  </span>
                </div>
                {m.tldr && <p className="text-[12.5px] text-neutral-400 mt-1 line-clamp-2">{displaySummaryText(m.tldr)}</p>}
                {m.participantNames.length > 0 && (
                  <p className="text-[11.5px] text-neutral-600 mt-1 truncate">{m.participantNames.join(', ')}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Kararlar */}
      {decisions.length > 0 && (
        <section className="space-y-2.5">
          <h2 className={`${sectionTitle} flex items-center gap-2`}>
            <Gavel className="w-3.5 h-3.5 text-amber-400" /> {t('Kararlar', 'Decisions')}
          </h2>
          <div className={`p-4 rounded-2xl border space-y-2.5 ${card}`}>
            {decisions.map((d, i) => (
              <button key={i} onClick={() => onOpenMeeting(d.meetingId)} className="w-full text-left flex items-start gap-2.5 group">
                <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0 mt-2" />
                <span className="text-[13px] text-neutral-300 leading-relaxed">
                  {d.text}{' '}
                  <span className="text-neutral-600 group-hover:text-[#46CBE2]">— {d.meetingTitle}, {formatDate(d.recordedAtISO)}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Açık aksiyonlar */}
      {actionItems.length > 0 && (
        <section className="space-y-2.5">
          <h2 className={`${sectionTitle} flex items-center gap-2`}>
            <ListChecks className="w-3.5 h-3.5 text-emerald-400" /> {t('Açık aksiyonlar', 'Open action items')}
          </h2>
          <div className={`p-4 rounded-2xl border space-y-2 ${card}`}>
            {actionItems.map((a) => (
              <button key={a.id} onClick={() => onOpenMeeting(a.meetingId)} className="w-full text-left flex items-start justify-between gap-3">
                <span className="text-[13px] text-neutral-300 leading-relaxed">
                  {a.text} <span className="text-neutral-600">— {a.meetingTitle}</span>
                </span>
                {a.assignee && <span className="text-[11px] text-neutral-500 shrink-0">{a.assignee}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
